import React from "react";

import DatePicker from "react-datepicker";
import { getDayNameLocalized } from "./datefns";
import { getConfigurationForDate } from "../../services/lodging";

import CustomInput from "./component.custom.input";
import CustomHeader from "./component.custom.header";
import { toYYYYMMDD } from "src/utils/datefns";

import "./DatePicker.scss";

interface DatePickerComponentProps {
  id?: string;
  label?: string;
  dateFormat?: string;
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  includeDateIntervals?: Array<{ start: Date; end: Date }>;
  excludeDateIntervals?: Array<{ start: Date; end: Date }>;
  openToDate?: Date;
  startOpen?: boolean;
  years?: boolean | number[];
  locale?: string;
  minDate?: Date | null;
  maxDate?: Date | null;
  customInput?: boolean;
  keyboardDisabled?: boolean;
  destinationColors?: boolean;
  weekdaysBlocked?: number[];
  blackoutPeriods?: Array<{ From: Date; To: Date }>;
  availability?: string[];
  item?: any;
}

// TODO Add missing typos (all of them :)))
const DatePickerComponent: React.FC<DatePickerComponentProps> = ({
  id,
  label,
  dateFormat = "yyyy/MM/dd",
  value,
  onChange,
  includeDateIntervals = [{ start: new Date(), end: new Date() }],
  excludeDateIntervals = [],
  openToDate,
  startOpen = false,
  years = false,
  locale = 'en',
  minDate = null,
  maxDate = null,
  customInput = false,
  keyboardDisabled = true,
  destinationColors = false,
  weekdaysBlocked = [],
  blackoutPeriods = [],
  availability = [],
  item = null
}) => {

  const checkForWeekDay = (date: Date) => {
    const day = date.getDay();
    const dateStr = toYYYYMMDD(date);
  
    // Get the configuration that applies to this specific date
    const configForDate = getConfigurationForDate(item, date);
    
    // If we have a configuration, use its blocked_days (even if null/empty)
    // Only fall back to props if no configuration exists
    const blockedDays = configForDate && configForDate.hasOwnProperty('blocked_days') 
      ? (configForDate.blocked_days || [])
      : weekdaysBlocked;
  
    let isWeekdayBlocked = false;
    if (blockedDays?.length) {
      isWeekdayBlocked = blockedDays.some((wd: number) => wd === day);
    }
  
    let isDateAvailable = true;
    if (availability && availability.length) {
      isDateAvailable = availability.includes(dateStr);
    }
  
    // Determine overall availability by combining conditions
    if (isWeekdayBlocked) {
      // If the weekday is blocked, it's not available
      return false;
    }
  
    if (!isDateAvailable) {
      // If the date is not in the availability list, it's not available
      return false;
    }
  
    // If no conditions blocked the date, it's available
    return true;
  }
  

  if(excludeDateIntervals.length === 0 && blackoutPeriods.length > 0) {
    excludeDateIntervals = blackoutPeriods;
  }

  return (
    <DatePicker
      id={`datepickerv2-calendar-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`}
      locale={locale}
      openToDate={openToDate}
      formatWeekDay={(day) => getDayNameLocalized(day as any, locale)}
      calendarStartDay={1}
      selected={value}
      onChange={(date) => onChange(date)}
      dateFormat={dateFormat}
      popperModifiers={[
        {
          name: "arrow",
          options: {
            padding: ({ popper }) => popper.width / 2,
          },
        },
      ]}
      // dayClassName={(date) => "H400Medium"}
      calendarClassName={"react-datepicker-custom H400Medium"}
      customInput={customInput ? customInput : <CustomInput id={id} label={label} keyboardDisabled={keyboardDisabled} destinationColors={destinationColors} locale={locale}/>}
      renderCustomHeader={({
        date,
        changeYear,
        changeMonth,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
      }) => (
        <CustomHeader
          years={years}
          date={date}
          changeYear={changeYear}
          changeMonth={changeMonth}
          decreaseMonth={decreaseMonth}
          increaseMonth={increaseMonth}
          prevMonthButtonDisabled={prevMonthButtonDisabled}
          nextMonthButtonDisabled={nextMonthButtonDisabled}
          locale={locale}
        />
      )}
      disabledKeyboardNavigation={keyboardDisabled}
      includeDateIntervals={includeDateIntervals}
      excludeDateIntervals={excludeDateIntervals}
      minDate={minDate}
      filterDate={checkForWeekDay}
      maxDate={maxDate}
      dayClassName={(date) => {
        if (value && value.setHours(0,0,0,0) === date.setHours(0,0,0,0)) return 'react-datepicker__day--selected'
        if (!availability) return "";
        const found = availability.find(data => {
          if (!data) return null;
          return data === toYYYYMMDD(date)
        })
        
        // Get the configuration for this specific date
        const configForDate = getConfigurationForDate(item, date);
        const blockedDays = configForDate && configForDate.hasOwnProperty('blocked_days') 
          ? (configForDate.blocked_days || [])
          : weekdaysBlocked;
        
        if(blockedDays.length && !checkForWeekDay(date)) return ""
        if(found) return `text-[#088401] bg-[#E9F4E8]`;
        return "";
      }}
    />
  );
};

export default DatePickerComponent;
